import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRouter } from 'next/navigation';

import { VideoCameraIcon } from '@heroicons/react/24/outline';
import { HomeIcon } from '@heroicons/react/24/solid';

import { delay, makeFullAddress } from '@/utils/common';
import { makeClinicSlug } from '@/utils/shared';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';
import { ClinicHeader } from '@/app/_components/widgets/listing/card/ClinicHeader';
import { BookingActions } from '@/app/_components/widgets/listing/card/_components/BookingActions';
import { NoAvailabilitiesInfo } from '@/app/_components/widgets/listing/card/_components/NoAvailabilitiesInfo';
import { AlgoliaClinicV2, License, PaymentType, SpecialityChannel } from '@/types/algolia-types';
import { AvailabilityModal } from '@/app/_components/widgets/listing/card/_components/AvailabilityModal';
import { formatPrice, getCheckoutURL } from '@/utils/checkout';
import { SlotAvailability } from '@/app/_components/widgets/listing/types';
import { ROUTES } from '@/routes';
import { ChangeSpecializationWrapper } from '@/app/_components/widgets/listing/card/_components/ChangeSpecializationWrapper';
import { fireAddToCartEvent, fireConfirmTimeSelectionEvent } from '@/utils/trackingGtm';
import { Speciality } from '@/app/_components/widgets/listing/card/Speciality';
import { CardAvailabilityInfo } from '@/app/_components/widgets/listing/card/CardAvailabilityInfo';
import { getStartOfDayTime } from '@/app/_components/widgets/listing/_utils/time';
import { trackAddToCart } from '@/utils/trackingMeta-pixelV2';
import { getCookieByName } from '@/utils/cookies';
import { usePatient } from '@/app/_hooks/usePatient';
import { useFocusTrap } from '@/app/_hooks/useFocusTrap';
import clsx from 'clsx';
import { monaFont } from '@/styles/fonts';
import { ELButton } from '@davincihealthcare/elty-design-system-react';
import { CardFlags, CARD_FLAGS_CONTAINER_DATA_CY } from '@/app/_components/widgets/listing/card/CardFlags';

export type AlgoliaCardProps = Pick<
  AlgoliaClinicV2,
  | 'license'
  | 'address'
  | 'cap'
  | 'city'
  | 'logoUrl'
  | 'name'
  | 'specializations'
  | 'domiciliaryCare'
  | 'ambulatoryId'
  | 'availablePayments'
  | 'slug'
  | 'objectID'
  | '_rankingInfo'
  | 'isIntegrated'
  | 'delayAvailability'
>;

type Props = {
  item: AlgoliaCardProps;
  showAllServicesEnabled?: boolean;
  showPhoneNumber?: boolean;
  showNoAvailabilitiesCallToAction?: boolean;
  originalDoctorId?: string;
  showDistance?: boolean;
  showBestClinicBanner?: boolean;
  isFocused?: boolean;
  onHoverChange?: (id: string, hovered: boolean) => void;
};

const ListingCard: React.FC<Props> = ({
  item,
  showAllServicesEnabled = true,
  showPhoneNumber = true,
  showNoAvailabilitiesCallToAction = true,
  showDistance = false,
  originalDoctorId,
  showBestClinicBanner = false,
  isFocused = false,
  onHoverChange,
}) => {
  const router = useRouter();

  const { patientEmail } = usePatient();

  const isLicensed = item?.license && (item.license as License) !== License.None;
  const isDomicile = item.domiciliaryCare;

  const isOnline = useMemo(
    () => item.specializations.specialityChannels?.includes(SpecialityChannel.Online),
    [item.specializations.specialityChannels],
  );

  const distance = showDistance && isLicensed && !isDomicile && !isOnline ? (item?._rankingInfo?.geoDistance ?? null) : null;

  const { label: specialityName, id: specialityId, hasDynamicPrice } = item.specializations;

  const paymentFlags = useMemo(() => {
    const normalized = new Set((item.availablePayments ?? []).map(p => String(p).toUpperCase()));
    return {
      hasOnsitePayment: normalized.has(PaymentType.Onsite),
      hasImmediateConfirmation: !normalized.has(PaymentType.Deferred),
    };
  }, [item.availablePayments]);

  const selectedDoctor = useMemo(() => {
    const doctorList = [...(item.specializations.doctors ?? [])];

    if (originalDoctorId) {
      const foundDoctor = doctorList.find(doctor => doctor.id === originalDoctorId);

      if (foundDoctor) {
        return foundDoctor;
      }
    }
    const sortedDoctors = doctorList.sort((a, b) => (a.nextAvailability ?? Infinity) - (b.nextAvailability ?? Infinity));
    return sortedDoctors[0];
  }, [item.specializations.doctors, originalDoctorId]);

  const price = selectedDoctor?.minPrice ?? selectedDoctor?.price ?? 0;
  const doctorId = selectedDoctor?.id;
  const algoliaHasSlots = !!selectedDoctor?.nextAvailability;
  const [resolvedHasSlots, setResolvedHasSlots] = useState<boolean | null>(null);
  const hasAvailableSlots = resolvedHasSlots ?? algoliaHasSlots;
  const workgroupId = item.ambulatoryId;
  const isWelfare = useIsWelfare();
  const completeAddress = useMemo(() => makeFullAddress(item.address, item.cap, item.city) ?? '', [item.address, item.cap, item.city]);

  const allClinicServicesLink = useMemo(
    () => makeClinicSlug(item, specialityName, specialityId, doctorId),
    [item, specialityId, specialityName, doctorId],
  );
  const clinicLink = useMemo(() => makeClinicSlug(item), [item]);
  const [showAllServicesModal, setShowAllServicesModal] = useState(false);

  const [isAvailabilityModalOpenInfo, setIsAvailabilityModalOpenInfo] = useState<null | { expectedFirstDay: number | null }>(null);

  useEffect(() => {
    setResolvedHasSlots(null);
  }, [doctorId, specialityId, workgroupId]);

  const openAvailabilityModal = ({ expectedFirstDay }: { expectedFirstDay: number | null }) => {
    setIsAvailabilityModalOpenInfo({ expectedFirstDay });
    try {
      const metaPixelProduct = {
        fbc: getCookieByName('_fbc') || '',
        fbp: getCookieByName('_fbp') || '',
        value: price,
        userEmail: patientEmail,
        price: 0,
        service: item.specializations.id || '',
      };
      trackAddToCart(metaPixelProduct);
    } catch (error) {
      console.error(error);
    }

    fireAddToCartEvent(`${price}`, {
      item_name: item?.specializations?.label,
      item_brand: item?.name,
      item_variant: item?.specializations?.label,
      item_category: item?.address ?? undefined,
      price: formatPrice({ amount: price }),
      index: 0,
    });
  };

  const handleSubmit = useCallback(
    async (selectedSlot: SlotAvailability) => {
      if (!selectedSlot) return;
      const slotPrice = selectedSlot.price ?? price;
      fireConfirmTimeSelectionEvent(formatPrice({ amount: slotPrice }), {
        item_name: item?.specializations?.label,
        item_brand: item?.name,
        item_variant: item?.specializations?.label,
        item_category: item?.address ?? undefined,
        price: formatPrice({ amount: slotPrice }),
        index: 0,
      });
      const checkoutUrl = getCheckoutURL(ROUTES.CHECKOUT_RECAP.pathname, {
        slot: selectedSlot.id,
      });

      router.push(checkoutUrl);
      await delay(3000);
    },
    [item, price, router],
  );

  const availabilityModalWrapperRef = useRef<HTMLDivElement>(null);
  const changeSpecializationWrapperRef = useRef<HTMLDivElement>(null);

  useFocusTrap({
    trapRef: availabilityModalWrapperRef,
    enabled: !!isAvailabilityModalOpenInfo,
    onClose: () => setIsAvailabilityModalOpenInfo(null),
  });

  useFocusTrap({ trapRef: changeSpecializationWrapperRef, enabled: showAllServicesModal, onClose: () => setShowAllServicesModal(false) });

  const onOpenAvailabilityModal = () =>
    openAvailabilityModal({
      expectedFirstDay: selectedDoctor?.nextAvailability ? getStartOfDayTime(selectedDoctor.nextAvailability) : null,
    });

  return (
    <div
      className={clsx(
        'overflow-x-hidden rounded-lg shadow-cardListing transition-shadow hover:ring-2 hover:ring-outlined-primary',
        isFocused && 'ring-2 ring-outlined-primary',
      )}
      onMouseEnter={() => onHoverChange?.(item.objectID, true)}
      onMouseLeave={() => onHoverChange?.(item.objectID, false)}
    >
      {showBestClinicBanner && resolvedHasSlots && (
        <div
          className={clsx(
            monaFont.className,
            'w-full bg-brandGreen-500 p-2 text-center text-sm font-bold uppercase tracking-wider text-neutral-inverse font-stretch-[85%] lg:text-base lg:tracking-widest',
          )}
        >
          La clinica migliore per te
        </div>
      )}

      {showAllServicesModal && (
        <div ref={changeSpecializationWrapperRef}>
          <ChangeSpecializationWrapper
            workgroupId={workgroupId}
            domiciliaryCare={isDomicile}
            onCancel={() => setShowAllServicesModal(false)}
            onNewSlot={slot => {
              setShowAllServicesModal(false);
              handleSubmit(slot);
            }}
          />
        </div>
      )}

      <div className="flex flex-col gap-4 bg-neutral-surface px-0 pb-4 pt-4 lg:flex-row lg:justify-between lg:gap-6 lg:p-6">
        <div className="flex flex-1 flex-col gap-2 px-4 lg:min-w-0 lg:px-0">
          <div className="flex flex-col">
            <ClinicHeader
              distance={distance}
              logoSrc={item.logoUrl}
              clinicLink={clinicLink}
              clinicName={item.name}
              isLicensed={isLicensed}
              isDomicile={isDomicile}
              completeAddress={completeAddress}
              allClinicServicesLink={showAllServicesEnabled ? allClinicServicesLink : undefined}
            />
          </div>

          <div className="flex flex-1 items-center justify-between gap-3">
            <div className="flex flex-col gap-0 overflow-hidden lg:gap-2">
              <Speciality
                isDomicile={isDomicile}
                workgroupId={item.ambulatoryId}
                specialityName={specialityName}
                hasDescription={item.specializations.hasDescription}
                specialityId={specialityId ?? null}
                logoUrl={item.logoUrl}
                price={price}
                hasDynamicPrice={hasDynamicPrice}
                clinicName={item.name}
                clinicLink={clinicLink}
                completeAddress={completeAddress}
                isLicensed={isLicensed}
                onChooseDate={() => {
                  openAvailabilityModal({ expectedFirstDay: null });
                }}
                disabled={!hasAvailableSlots}
              />
            </div>
          </div>

          {(isDomicile || isOnline) && (
            <div className="flex-wrap items-center gap-1">
              {isDomicile && (
                <span className="inline-flex items-center gap-1 rounded-full bg-brandBlue-100 px-2 py-0.5 text-xs font-medium text-primary md:text-sm">
                  <HomeIcon className="size-3 md:size-4" />A domicilio
                </span>
              )}
              {isOnline && (
                <span className="inline-flex items-center gap-1 rounded-full bg-brandBlue-100 px-2 py-0.5 text-xs font-medium text-primary md:text-sm">
                  <VideoCameraIcon className="size-3 md:size-4" />
                  Prestazione online
                </span>
              )}
            </div>
          )}

          <div className="w-full">
            <ELButton
              color="primary"
              variant="text"
              size="small"
              label="Vedi altre prestazioni"
              onClick={() => setShowAllServicesModal(true)}
              fullRadius
            />
          </div>
          <div data-cy={CARD_FLAGS_CONTAINER_DATA_CY.desktop} className="hidden items-center gap-1 sm:hidden lg:flex">
            <CardFlags
              hasAvailableSlots={hasAvailableSlots}
              paymentFlags={paymentFlags}
              isWelfare={isWelfare}
              isIntegrated={item.isIntegrated}
            />
          </div>
        </div>

        <div className="flex w-full flex-col gap-4 px-4 lg:w-auto lg:flex-shrink-0 lg:gap-5 lg:px-0">
          {!isLicensed || !specialityId || !doctorId ? (
            <NoAvailabilitiesInfo
              title="Al momento non è possibile prenotare"
              {...(showNoAvailabilitiesCallToAction && {
                description: 'Visualizza il profilo',
                path: allClinicServicesLink,
              })}
            />
          ) : (
            <>
              {isAvailabilityModalOpenInfo && (
                <div ref={availabilityModalWrapperRef}>
                  <AvailabilityModal
                    specialityId={specialityId}
                    doctors={item.specializations.doctors}
                    workgroupId={workgroupId}
                    initialSelectedDay={null}
                    firstDayExpected={isAvailabilityModalOpenInfo.expectedFirstDay}
                    onClose={() => setIsAvailabilityModalOpenInfo(null)}
                    onSubmit={handleSubmit}
                    delayAvailability={item.delayAvailability}
                  />
                </div>
              )}
              <CardAvailabilityInfo
                specialityId={specialityId}
                workgroupId={workgroupId}
                firstTimeAvailable={selectedDoctor?.nextAvailability}
                delayAvailability={item.delayAvailability}
                hasDynamicPrice={hasDynamicPrice}
                isLicensedClinic={isLicensed}
                price={price}
                hasAvailableSlots={hasAvailableSlots}
                onAvailabilityResolved={setResolvedHasSlots}
              />

              <div data-cy={CARD_FLAGS_CONTAINER_DATA_CY.mobile} className="flex flex-wrap items-center justify-center gap-1 lg:hidden">
                <CardFlags
                  hasAvailableSlots={hasAvailableSlots}
                  paymentFlags={paymentFlags}
                  isWelfare={isWelfare}
                  isIntegrated={item.isIntegrated}
                />
              </div>

              <BookingActions
                hasAvailableSlots={hasAvailableSlots}
                onClick={onOpenAvailabilityModal}
                onNoAvailabilitiesClick={() => setShowAllServicesModal(true)}
                showPhoneNumber={showPhoneNumber && !isWelfare}
                listingCardLayout
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
